import _ from 'lodash';
import { parse, stringify } from 'lossless-json';

import Service, {
  Constraint,
  Operators,
  Rollout,
  RolloutTarget,
  VariableInfo,
} from '../../lib/App';
import { ModifiedRolloutTargetMapValue } from '@/common/dto/changelog';
import { FACETS } from '@/constants/facets';
import { FacetsForName } from '@/utils/facet';
import { removeLosslessObject } from '@/utils/object';
import { parseArrayFromString } from '@/utils/string';

export type DiffEditType = 'deleted' | 'modified' | 'added' | '' | 'reordered';

export const PURE_GEOGRAPHIC_FACETS = [FACETS.Country, FACETS.City];
export const COMPLEX_GEOGRAPHIC_FACETS = [FACETS.VehicleTypeID];
export const ALL_GEOGRAPHIC_FACETS = [
  ...PURE_GEOGRAPHIC_FACETS,
  ...COMPLEX_GEOGRAPHIC_FACETS,
];

export function getRolloutDescription(
  rollout: Rollout,
  variable: VariableInfo,
): string {
  let rolloutTargetsDescription = ``;

  if (rollout.rollouts !== undefined) {
    for (const rolloutTarget of rollout.rollouts) {
      rolloutTargetsDescription += getRolloutTargetDescription(
        rolloutTarget,
        false,
        variable,
      );
    }
  }

  rolloutTargetsDescription += getRolloutTargetDescription(
    rollout.default!,
    true,
    variable,
  );

  return rolloutTargetsDescription;
}

function getRolloutTargetDescription(
  rolloutTarget: RolloutTarget,
  isDefault: boolean,
  variable: VariableInfo,
): string {
  let rolloutTargetDescription = ``;
  let prefixString = `Rollout with value:\n`;

  if (isDefault) {
    prefixString = `Rollout defaults to value:\n`;
  }

  rolloutTargetDescription += prefixString;
  rolloutTargetDescription += getValueDescription(rolloutTarget, variable);
  rolloutTargetDescription += getConstraintDescription(
    rolloutTarget.constraints,
  );

  return rolloutTargetDescription;
}

function getValueDescription(
  rolloutTarget: RolloutTarget,
  variable: VariableInfo,
): string {
  // Validation to handle undefined case
  // For variables that require variable.Value
  if (
    ['boolean', 'int64', 'float64'].indexOf(variable.type) >= 0 &&
    rolloutTarget.value === undefined
  ) {
    return `\tundefined\n`;
  }

  // For variables that require variable.StringValue
  if (
    ['string', 'map', 'json', 'file'].indexOf(variable.type) >= 0 &&
    rolloutTarget.string === undefined
  ) {
    return `\tundefined\n`;
  }

  switch (variable.type) {
    case 'boolean':
      return rolloutTarget.value === 1 ? `\tTrue\n` : `\tFalse\n`;
    case 'int64':
    case 'float64':
      return `\t${rolloutTarget.value}\n`;
    case 'json':
    case 'map':
      try {
        const jsonObject = parse(rolloutTarget.string!);
        removeLosslessObject(jsonObject);
        const formattedJson = stringify(jsonObject, null, 2).replace(
          /\n/g,
          `\n\t`,
        );
        return `\t${formattedJson}\n`;
      } catch (e) {
        return `\t${rolloutTarget.string}\n`;
      }

    default:
      return `\t${rolloutTarget.string}\n`;
  }
}

function getConstraintDescription(constraints: Constraint[]): string {
  if (constraints === undefined || constraints.length === 0) {
    return `\tfor population globally\n`;
  }

  let constraintString = `\tto population where\n`;
  const facetsDisplayNames: { [key: string]: string } = FacetsForName();

  for (const constraint of constraints) {
    constraintString += `\t\t${facetsDisplayNames[constraint.target!]} ${
      constraint.op
    } ${constraint.value}\n`;
  }
  return constraintString;
}

function isRolloutTargetDeleted(
  search: RolloutTarget,
  list: RolloutTarget[],
): boolean {
  if (list === null || list === undefined) {
    list = [];
  }
  return (
    list.find(
      (rolloutTarget: RolloutTarget) => rolloutTarget.id === search.id,
    ) === undefined
  );
}

function isRolloutTargetModified(
  rolloutTarget: RolloutTarget,
  newRolloutTargets: RolloutTarget[],
): boolean {
  const newTarget = newRolloutTargets.find(
    (e: RolloutTarget) => e.id === rolloutTarget.id,
  );
  if (newTarget === undefined) {
    return false;
  }

  return (
    hasValueChanged(rolloutTarget, newTarget) ||
    hasConstraintChanged(rolloutTarget, newTarget)
  );
}

export function isRolloutTargetReordered(
  rolloutTarget: RolloutTarget,
  rolloutTargetIndex: number,
  newRolloutTargets: RolloutTarget[],
): boolean {
  const index = newRolloutTargets.findIndex(
    (e: RolloutTarget) => e.id === rolloutTarget.id,
  );
  let compareTarget = null;
  if (index > -1) compareTarget = newRolloutTargets[index];

  return (
    index !== rolloutTargetIndex &&
    (rolloutTarget?.reorder || compareTarget?.reorder)
  );
}

export const normalizeRollout = (rollout: Rollout) => {
  return {
    ...rollout,
    rollouts: (rollout.rollouts || []).map((rolloutTarget: RolloutTarget) => ({
      ...rolloutTarget,
      constraints: rolloutTarget.constraints || [],
    })),
  };
};

export function getRolloutTargetPosition(
  rollout: Rollout,
  targetId: string,
): number {
  if (!rollout?.rollouts || !targetId) {
    return -1;
  }

  return rollout.rollouts.findIndex(
    (rolloutTarget) => rolloutTarget?.id === targetId,
  );
}

export function hasTargetPositionChanged(
  beforeRollout: Rollout,
  afterRollout: Rollout,
  targetId: string,
): boolean {
  if (!targetId) return false;

  const beforeIndex = getRolloutTargetPosition(beforeRollout, targetId);
  const afterIndex = getRolloutTargetPosition(afterRollout, targetId);

  // Both positions must be valid to compare
  if (beforeIndex === -1 || afterIndex === -1) {
    return false;
  }

  return beforeIndex !== afterIndex;
}

export function hasPositionChanged(
  value: ModifiedRolloutTargetMapValue,
): boolean {
  const { before, after } = value;

  if (!before.rolloutTarget?.id || !after.rolloutTarget?.id) {
    return false;
  }

  return hasTargetPositionChanged(
    before.rollout!,
    after.rollout!,
    before.rolloutTarget.id,
  );
}

function hasValueChanged(
  oldTarget: RolloutTarget,
  newTarget: RolloutTarget,
): boolean {
  return (
    oldTarget?.value !== newTarget?.value ||
    oldTarget?.string !== newTarget?.string
  );
}

function hasConstraintChanged(
  oldTarget: RolloutTarget,
  newTarget: RolloutTarget,
): boolean {
  let oldConstraints: Constraint[] = [];
  let newConstraints: Constraint[] = [];

  if (oldTarget.constraints !== null && oldTarget.constraints !== undefined) {
    oldConstraints = oldTarget.constraints;
  }

  if (newTarget.constraints !== null && newTarget.constraints !== undefined) {
    newConstraints = newTarget.constraints;
  }

  if (oldConstraints.length !== newConstraints.length) {
    return true;
  }

  let hasModified = false;

  oldConstraints.forEach((c: Constraint) => {
    const isPre =
      newConstraints.find(
        (con: Constraint) =>
          con.op === c.op && con.target === c.target && con.value === c.value,
      ) !== undefined;
    if (isPre === false) {
      hasModified = true;
    }
  });
  return hasModified;
}

function isDefaultModified(
  oldDefault: RolloutTarget,
  newDefault: RolloutTarget,
): boolean {
  return hasValueChanged(oldDefault, newDefault);
}

function isDefaultDeleted(defaultTarget: RolloutTarget): boolean {
  return (
    defaultTarget === null ||
    defaultTarget === undefined ||
    defaultTarget.value === null
  );
}

export function getOldEditState(
  rolloutTarget: RolloutTarget,
  rolloutTargets: RolloutTarget[],
): DiffEditType {
  return isRolloutTargetDeleted(rolloutTarget, rolloutTargets)
    ? 'deleted'
    : isRolloutTargetModified(rolloutTarget, rolloutTargets)
      ? 'modified'
      : '';
}

export function getOldReorderState(
  rolloutTarget: RolloutTarget,
  rolloutTargetIndex: number,
  rolloutTargets: RolloutTarget[],
): boolean {
  return isRolloutTargetReordered(
    rolloutTarget,
    rolloutTargetIndex,
    rolloutTargets,
  );
}

export function getNewEditState(
  rolloutTarget: RolloutTarget,
  rolloutTargets: RolloutTarget[],
): DiffEditType {
  return isRolloutTargetDeleted(rolloutTarget, rolloutTargets)
    ? 'added'
    : isRolloutTargetModified(rolloutTarget, rolloutTargets)
      ? 'modified'
      : '';
}

export function getNewReorderState(
  rolloutTarget: RolloutTarget,
  rolloutTargetIndex: number,
  rolloutTargets: RolloutTarget[],
): boolean {
  return isRolloutTargetReordered(
    rolloutTarget,
    rolloutTargetIndex,
    rolloutTargets,
  );
}

export function getOldDefaultEditState(
  oldDefault: RolloutTarget,
  newDefault: RolloutTarget,
): DiffEditType {
  return isDefaultDeleted(newDefault)
    ? 'deleted'
    : isDefaultModified(oldDefault, newDefault)
      ? 'modified'
      : '';
}

export function getNewDefaultEditState(
  oldDefault: RolloutTarget,
  newDefault: RolloutTarget,
): DiffEditType {
  return isDefaultDeleted(oldDefault)
    ? 'added'
    : isDefaultModified(newDefault, oldDefault)
      ? 'modified'
      : '';
}

export function getCountriesFromConstraint(constraint: Constraint): string[] {
  const { target } = constraint;

  switch (target) {
    case FACETS.Country:
      return getCountriesFromCountryConstraint(constraint);
    case FACETS.City:
      return getCountriesFromCityConstraint(constraint);
    case FACETS.VehicleTypeID:
      return getCountriesFromVehicleConstraint(constraint);
    default:
      // Non-geographic constraints return empty array (global scope)
      return [];
  }
}

export function getAllCountryCodes(): string[] {
  try {
    const allCountries = Service.cache?.getAllCountries() || [];
    return allCountries.map((country) => country.Code.toUpperCase());
  } catch (error) {
    console.warn('Error getting all country codes:', error);
    return [];
  }
}

function getCountriesFromCountryConstraint(constraint: Constraint): string[] {
  const { op, value } = constraint;

  if (!value) {
    return [];
  }

  const countryIds = parseArrayFromString(value).map((c) => c.toUpperCase());

  try {
    if (op === Operators.in) {
      return countryIds;
    } else {
      return getAllCountryCodes().filter((c) => !countryIds.includes(c));
    }
  } catch (error) {
    console.warn('Error parsing country constraint:', error);
    return [];
  }
}

function getCountriesFromCityConstraint(constraint: Constraint): string[] {
  const { op, value } = constraint;

  if (!value) {
    return [];
  }

  if (![Operators.in, Operators['not in']].includes(op)) {
    return [];
  }

  const cityIds = parseArrayFromString(value)
    .map((c) => parseInt(c, 10))
    .filter((id) => !isNaN(id));

  try {
    if (op === Operators.in) {
      return _.uniq(
        cityIds
          .map((cityId) => {
            const city = Service.cache?.getCity(cityId);
            return city?.countryCode ? city.countryCode.toUpperCase() : null;
          })
          .filter(Boolean),
      );
    }

    // 'not in' operation
    const excludedCityIds = cityIds;

    // countryCode -> array of excluded city ids
    const countryToExcludedCityIds: Record<string, number[]> =
      excludedCityIds.reduce(
        (acc, cityId) => {
          const city = Service.cache?.getCity(cityId);
          if (city?.countryCode) {
            acc[city.countryCode.toUpperCase()] = [
              ...(acc[city.countryCode.toUpperCase()] || []),
              cityId,
            ];
          }
          return acc;
        },
        {} as Record<string, number[]>,
      );

    const fullyExcludedCountries = Object.keys(countryToExcludedCityIds).filter(
      (countryCode) => {
        const allCityIds =
          Service.cache?.getCitiesByCountryCode(countryCode) || [];
        return allCityIds.length === cityIds.length;
      },
    );

    const allCountries = getAllCountryCodes();
    return allCountries.filter(
      (country) => !fullyExcludedCountries.includes(country),
    );
  } catch (error) {
    console.warn('Error parsing city constraint:', error);
    return [];
  }
}

function getCountriesFromVehicleConstraint(constraint: Constraint): string[] {
  const { op, value } = constraint;

  if (!value) {
    return [];
  }

  // Assume 'non in' vehicle constraints are global
  if (op !== Operators.in) {
    return getAllCountryCodes();
  }

  try {
    const vehicleItems = parseArrayFromString(value);

    const countries = new Set<string>();

    vehicleItems.forEach((item) => {
      const vehicleId = parseInt(item, 10);
      if (!isNaN(vehicleId)) {
        try {
          const countryCode = Service.cache?.getCountryByVehicle(vehicleId);
          if (countryCode) {
            countries.add(countryCode.toUpperCase());
          }
        } catch (error) {
          // Vehicle might not exist, skip silently
          return [];
        }
      }
    });

    return Array.from(countries);
  } catch (error) {
    console.warn('Error parsing vehicle constraint:', error);
    return [];
  }
}

/**
 * Returns the list of geographic constraint types (e.g. 'country', 'city', etc)
 * whose values have changed between two sets of constraints.
 *
 * Example:
 *   beforeConstraints = [
 *     {target: 'country', op: 'in', value: 'US,CA'},
 *     {target: 'city', op: 'in', value: 'NYC,SF'},
 *     {target: 'vehicle', op: 'in', value: '1,2'}
 *   ]
 *   afterConstraints  = [
 *     {target: 'country', op: 'in', value: 'US,MX'},
 *     {target: 'city', op: 'in', value: 'NYC,SF'},
 *     {target: 'vehicle', op: 'in', value: '1,2,3'}
 *   ]
 *   => returns ['country', 'vehicle']
 */
export function detectChangedGeographicTypes(
  beforeConstraints: Constraint[],
  afterConstraints: Constraint[],
): string[] {
  function getGeoSignaturesByType(constraints: Constraint[]) {
    const result: Record<string, string[]> = {};

    constraints.forEach((c) => {
      if (ALL_GEOGRAPHIC_FACETS.includes(c.target)) {
        const signature = JSON.stringify({ op: c.op, value: c.value });
        if (!result[c.target]) {
          result[c.target] = [];
        }
        result[c.target].push(signature);
      }
    });

    // Sort signatures for each type to ensure order doesn't affect comparison
    Object.keys(result).forEach((key) => {
      result[key].sort();
    });

    // Join signatures into a single string for comparison
    const joined: Record<string, string> = {};
    Object.keys(result).forEach((key) => {
      joined[key] = result[key].join('|');
    });

    return joined;
  }

  const beforeGeoByType = getGeoSignaturesByType(beforeConstraints);
  const afterGeoByType = getGeoSignaturesByType(afterConstraints);

  const allTypes = _.union(
    Object.keys(beforeGeoByType),
    Object.keys(afterGeoByType),
  );

  const changedTypes = allTypes.filter(
    (type) => (beforeGeoByType[type] || '') !== (afterGeoByType[type] || ''),
  );

  return changedTypes;
}
